import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useHasMounted } from './useHasMounted';

/**
 * Hook for hash-based navigation using Next.js router.
 * Provides consistent hash state management with proper back/forward support.
 *
 * @param {string[]} validIds - Array of valid hash values
 * @param {string} defaultId - Default value when hash is empty or invalid
 * @param {string} basePath - Base path for the page (e.g., '/articles')
 * @returns {[string, (id: string) => void]} - [activeId, setActiveId]
 */
export function useHashNavigation(validIds, defaultId, basePath) {
  const router = useRouter();
  const hasMounted = useHasMounted();

  // Derived from router.asPath (handles initial load + back/forward). The server
  // never sees the hash, so hydrate with the default and read it once mounted.
  const hash = hasMounted ? router.asPath.split('#')[1] : undefined;
  const activeId = hash && validIds.includes(hash) ? hash : defaultId;

  // Update URL hash via Next.js router
  const setActiveId = useCallback(
    id => {
      const newPath = id === defaultId ? basePath : `${basePath}#${id}`;
      router.push(newPath, undefined, { shallow: true });
    },
    [router, basePath, defaultId]
  );

  return [activeId, setActiveId];
}
